// MapViewDr6.jsx
import React, { useState, useCallback, useEffect } from 'react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { useMutation, useQueryCache } from 'react-query'

import customMarkerIcon from './img/location-dot-solid.svg'
import infoIcon from './img/circle-info-regular.svg'

import ExpandedRecordDr6 from '../ExpandedRecordDr6'
import ExpandedRecordsBusiness from '../BusinessDataDR15/ExpandedRecords/ExpandedRecordBusiness'
import { postProperty } from '../../Api/index'
import BaseAlert from '../Snackbar/NewImplementations/BaseAlert/BaseAlert'

import styles from './styles.module.css'

/* -------------------------------------------------------------------------- */
/* Map constants                                                               */
/* -------------------------------------------------------------------------- */
const containerStyle = { width: '100%', height: '100%' }
const initialCenter = { lat: 39.8283, lng: -98.5795 } // U.S. midpoint
const initialZoom = 4

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */
function MapViewDr6 ({ dataType }) {
  /* ----------------- store selector (property vs business) ---------------- */
  const { dataResults, isQueryLoading } =
    dataType === 'property'
      ? require('../Store/Store').default()
      : require('../BusinessDataDR15/BusinessStore/Store').default()
  const recordsData = dataResults?.data?.records

  /* ------------------------- local UI state -------------------------------- */
  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)

  const [infoAlert, setInfoAlert] = useState({ open: false })

  /* ------------------ Google Maps instance & react-query ------------------- */
  const queryCache = useQueryCache()
  const [map, setMap] = useState(null)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyD0AKt7KKjLie_FUX7rH1HAFXD9svOE6Go'
  })

  /* ---------------------- full-record fetch for property ------------------- */
  const [mutate] = useMutation(postProperty, {
    onSuccess: data => {
      const newRecord = data?.data?.records?.[0]
      if (!newRecord) return

      const cacheKey = `recordByID-${newRecord.keys[0]}`
      queryCache.setQueryData(cacheKey, newRecord)

      setSelectedRecord(newRecord)
      setModalOpen(true)
    }
  })

  /* -------------------- marker / list click handler ------------------------ */
  const handleRecordClick = record => {
    if (isQueryLoading) return

    if (dataType === 'business') {
      setSelectedRecord(record)
      setModalOpen(true)
      return
    }

    const cacheKey = `recordByID-${record.keys[0]}`
    const cachedData = queryCache.getQueryData(cacheKey)

    if (cachedData) {
      setSelectedRecord(cachedData)
      setModalOpen(true)
    } else {
      mutate({ query: `keys:"${record.keys[0]}"` })
    }
  }

  /* ---- clear cached per-record queries when list of results changes ------- */
  useEffect(() => {
    queryCache.getQueries().forEach(q => {
      const key0 = q?.queryKey?.[0]
      if (typeof key0 === 'string' && key0.startsWith('recordByID')) {
        queryCache.removeQueries(key0)
      }
    })
  }, [dataResults, queryCache])

  /* ------------------------ map utility functions -------------------------- */
  const fitBoundsToMarkers = (mapInstance, records) => {
    const bounds = new window.google.maps.LatLngBounds()
    records.forEach(r => {
      if (r.latitude && r.longitude) {
        bounds.extend(
          new window.google.maps.LatLng(
            parseFloat(r.latitude),
            parseFloat(r.longitude)
          )
        )
      }
    })
    if (!bounds.isEmpty()) mapInstance.fitBounds(bounds)
  }

  const onLoad = useCallback(
    mapInstance => {
      setMap(mapInstance)
      if (recordsData?.length) fitBoundsToMarkers(mapInstance, recordsData)
    },
    [recordsData]
  )

  const onUnmount = useCallback(() => setMap(null), [])

  useEffect(() => {
    if (map && recordsData?.length) fitBoundsToMarkers(map, recordsData)
  }, [map, recordsData])

  /* --------------------------- render block -------------------------------- */
  if (!isLoaded) {
    return <div style={containerStyle}>Loading…</div>
  }

  return (
    <div className={styles.mapContainer}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={initialCenter}
        zoom={initialZoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false
        }}
      >
        {recordsData?.map(record => (
          <Marker
            key={record.id}
            position={{
              lat: parseFloat(record.latitude),
              lng: parseFloat(record.longitude)
            }}
            onClick={() => handleRecordClick(record)}
            icon={{
              url: customMarkerIcon,
              scaledSize: new window.google.maps.Size(15, 21)
            }}
          />
        ))}
      </GoogleMap>

      {/* -------------- floating “Showing X of Y matches” pill -------------- */}
      {dataResults?.data?.num_found && (
        <div className={styles.markerLimit}>
          {`Showing ${recordsData.length} of ${dataResults.data.num_found.toLocaleString()} matches`}
          <img
            src={infoIcon}
            alt='info'
            onClick={() => setInfoAlert({ open: true })}
          />
        </div>
      )}

      {/* ------------------------- detail modals ---------------------------- */}
      {isModalOpen && dataType === 'property' && (
        <ExpandedRecordDr6
          recordData={selectedRecord}
          onClose={() => setModalOpen(false)}
        />
      )}

      {isModalOpen && dataType === 'business' && (
        <ExpandedRecordsBusiness
          recordData={selectedRecord}
          onClose={() => setModalOpen(false)}
        />
      )}

      <BaseAlert
        open={infoAlert.open}
        title='Up to 50 markers shown'
        body='The map only shows the locations of the properties currently displayed on the table to the left. It does not show all available matches for your search. Page through the results to see all available matches.'
        color='#000000'
        actions={[
          { label: 'Ok', onClick: () => setInfoAlert({ open: false }) }
        ]}
        onClose={() => setInfoAlert({ open: false })}
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '320px'
        }}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
export default React.memo(MapViewDr6)
